import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useUser } from '../../../contexts/UserContext';
import {
  tlsrptApi,
  fmtNumber,
  fmtPct,
  fmtRelative,
  DomainSummary,
} from '../../../services/tlsrptApi';

/** Trend semantics here are inverted vs. DMARC: failure_rate going DOWN is GOOD. */
const TrendIcon: React.FC<{ trend: DomainSummary['trend'] }> = ({ trend }) => {
  if (trend === 'down')
    return (
      <Tooltip title="Failure rate trending down (improving)">
        <TrendingDownIcon fontSize="small" sx={{ color: 'success.main' }} />
      </Tooltip>
    );
  if (trend === 'up')
    return (
      <Tooltip title="Failure rate trending up (worsening)">
        <TrendingUpIcon fontSize="small" sx={{ color: 'error.main' }} />
      </Tooltip>
    );
  if (trend === 'flat')
    return <RemoveIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  return (
    <Typography component="span" color="text.secondary">
      —
    </Typography>
  );
};

const ReadinessChip: React.FC<{ row: DomainSummary }> = ({ row }) => {
  if (!row.last_seen_iso) {
    return <Chip size="small" variant="outlined" label="Awaiting first report" />;
  }
  if (row.enforce_ready) {
    return (
      <Tooltip
        title={`Zero failures across ${row.enforce_ready_window_days} days of recent traffic — safe to promote testing → enforce`}
      >
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label={`Ready for enforce (${row.enforce_ready_window_days}d clean)`}
          color="success"
          variant="outlined"
        />
      </Tooltip>
    );
  }
  if (row.failures_in_enforce_window > 0) {
    return (
      <Tooltip
        title={`${row.failures_in_enforce_window} failure sessions in the last ${row.enforce_ready_window_days} days — fix before promoting`}
      >
        <Chip
          size="small"
          icon={<ErrorOutlineIcon />}
          label={`${fmtNumber(row.failures_in_enforce_window)} failures in ${row.enforce_ready_window_days}d`}
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`No traffic observed in the last ${row.enforce_ready_window_days} days`}>
      <Chip
        size="small"
        icon={<HourglassEmptyIcon />}
        label="Insufficient data"
        variant="outlined"
      />
    </Tooltip>
  );
};

const TlsRptOverview: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<DomainSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/app/welcome');
      return;
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin()) return;
    tlsrptApi
      .listDomains()
      .then(r => setDomains(r.domains))
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [isAdmin]);

  if (!isAdmin()) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          TLS-RPT overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Inbound TLS handshake reports per RFC 8460. Used to gauge readiness for
          promoting an MTA-STS policy from <code>testing</code> to <code>enforce</code>.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load domains: {error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Monitored domains
          </Typography>
          {!domains && !error && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {domains && domains.length === 0 && (
            <Typography color="text.secondary" align="center" py={4}>
              No monitored domains yet.
            </Typography>
          )}
          {domains && domains.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">Reports 30d</TableCell>
                    <TableCell align="right">Successful sessions 30d</TableCell>
                    <TableCell align="right">Failure sessions 30d</TableCell>
                    <TableCell align="right">Failure rate 30d</TableCell>
                    <TableCell align="center">Trend</TableCell>
                    <TableCell>Readiness</TableCell>
                    <TableCell align="right">Last report</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domains.map(d => (
                    <TableRow hover key={d.domain}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Link
                          component={RouterLink}
                          to={`/app/admin/tlsrpt/domains/${encodeURIComponent(d.domain)}`}
                          underline="hover"
                        >
                          {d.domain}
                        </Link>
                      </TableCell>
                      <TableCell align="right">{fmtNumber(d.report_count_30d)}</TableCell>
                      <TableCell align="right">{fmtNumber(d.successful_sessions_30d)}</TableCell>
                      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum"' }}>
                        {d.failure_sessions_30d > 0 ? (
                          <Typography component="span" color="error.main" fontWeight={500}>
                            {fmtNumber(d.failure_sessions_30d)}
                          </Typography>
                        ) : (
                          <Typography component="span" color="text.secondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum"' }}>
                        {fmtPct(d.failure_rate_30d)}
                      </TableCell>
                      <TableCell align="center">
                        <TrendIcon trend={d.trend} />
                      </TableCell>
                      <TableCell>
                        <ReadinessChip row={d} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {fmtRelative(d.last_seen_iso)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {domains && domains.length > 0 && !domains.some(d => d.last_seen_iso) && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No TLS-RPT reports have arrived yet. Reports typically begin landing within a day
            or two of a domain's <code>_smtp._tls</code> TXT record pointing to{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
              tlsrpt@reports.travelminds.com
            </Box>
            . Receivers like Google and Microsoft send daily aggregate reports for any domain
            you've enabled MTA-STS on.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default TlsRptOverview;
